import React, { useMemo, useCallback, useState, useEffect } from 'react';
import _ from 'lodash';

import { makeStyles, Container, Grid, Paper } from '@material-ui/core';
import BreadCrumb from 'app/components/BreadCrumb';
import { injectIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import requestEndpoints from 'app/api/services/requestEndpoints';
import notification from 'app/config/notification';
import { showLoading, hideLoading } from 'app/store/slices/settings';
import { fetchSaveRequest, updateRequestDetails } from 'app/store/slices/requests';
import { extractFileFromJson } from 'app/store/slices/forms';
import { preparePayload } from 'app/utils/forms/functions';
import { FormServiceContext } from 'app/utils/contexts';

import Company from 'app/components/ServicesForms/Registration/Company';
import Person from 'app/components/ServicesForms/Registration/Person';

import alertsEndpoint from 'app/api/services/alertsEndpoint';
import withRequestInitiation from 'app/components/Hoc/withRequestInitiation';
import { registrationPersonValidationSchema, registrationValidationSchema } from 'app/utils/forms/validationsSchemas';
import { registrationPayload, registrationPersonPayload } from 'app/utils/forms/payloadsStruture';
import { errorHandler } from 'app/utils/errorHandler';

import { SERVICES_CODES, SERVICES_PROPERTIES } from 'app/config/services';
import { STATUS_CODES } from 'app/config/status';

import styles from './styles';
import { validateCompanyRequest } from 'app/utils/forms/validators/company';
const useStyles = makeStyles(styles);

function Registration(props) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const {
        intl,
        loadingDocuments,
        setLoadingDocuments,
        canEdit,
        editing,
        resetAndGo,
        onCancel,
        validateForm,
        breadCrumb,
        rightInfo,
        handleOnBack,
    } = props;

    const service = useSelector((state) => state.services.details);
    const request = useSelector((state) => state.requests.details);
    const documentsStore = useSelector((state) => state.services.documents);
    const activeCompany = useSelector((state) => state.company.activeCompany);
    const registrationUserInfo = useSelector((state) => state.auth.registrationInfo);
    const userAdmin = useSelector((state) => state.auth.userAdmin);

    const [validationSchema, setValidationSchema] = useState(registrationValidationSchema);

    const [registrationType, setRegistrationType] = useState(() => {
        if (request.jsonSolicitacao) {
            const parsed = JSON.parse(request.jsonSolicitacao);
            if (parsed.tpCadastro) {
                return parsed.tpCadastro;
            } else if (parsed.cdTipoPessoaFisica) {
                return 'person';
            }
        } else if (activeCompany.cdTipoPessoaFisica) {
            return 'person';
        } else {
            if (
                !activeCompany.deCnpjMatriz &&
                registrationUserInfo &&
                registrationUserInfo.deCpfCnpj &&
                registrationUserInfo.deCpfCnpj.length <= 11
            ) {
                return 'person';
            }
        }
        return 'company';
    });

    useEffect(() => {
        const init = () => {
            if (registrationType === 'person') {
                setValidationSchema(registrationPersonValidationSchema);
            } else {
                setValidationSchema(registrationValidationSchema);
            }
        };
        init();
    }, [registrationType]);

    const initialValues = useMemo(() => {
        if (editing) {
            const value = JSON.parse(request.jsonSolicitacao);
            if (value.enderecoMatriz && value.enderecoMatriz.municipio) {
                value.enderecoMatriz.uf = value.enderecoMatriz.municipio.uf?.sgUf;
            }
            if (value.enderecoFilial?.municipio?.uf) {
                value.enderecoFilial.uf = value.enderecoFilial.municipio.uf?.sgUf;
            }
            return value;
        }
        if (Object.keys(activeCompany).length > 0) {
            const temp = _.cloneDeep(activeCompany);

            //####### cleaning unnecessary data
            if (
                (!request.cdSolicitacao || request.cdSolicitacao === '') &&
                service.cdServico === SERVICES_CODES.INSCRICAO_CADASTRAL &&
                temp.cdEmpresa
            ) {
                delete temp.cdEmpresa;
            }
            if (temp.cdUltimaSolicitacao != null) delete temp.cdUltimaSolicitacao;
            if (temp.cdCertificado != null) delete temp.cdCertificado;
            if (temp.boSituacaoOrgao != null) delete temp.boSituacaoOrgao;
            if (temp.tipoEmpresa != null) delete temp.tipoEmpresa;
            if (temp.ramoAtividade != null) delete temp.ramoAtividade;
            if (registrationType === 'person') {
                Object.keys(registrationPayload).forEach((k) => {
                    if (temp[k] != null && !Object.keys(registrationPersonPayload).includes(k)) {
                        delete temp[k];
                    }
                });
            } else {
                Object.keys(registrationPersonPayload).forEach((k) => {
                    if (temp[k] != null && !Object.keys(registrationPayload).includes(k)) {
                        delete temp[k];
                    }
                });
            }

            if (temp.documentos) delete temp.documentos;
            if (temp.certificado) delete temp.certificado;
            if (temp.ultimaSolicitacao) delete temp.ultimaSolicitacao;

            if (temp.enderecoMatriz && temp.enderecoMatriz.municipio) {
                temp.enderecoMatriz.uf = temp.enderecoMatriz.municipio.uf?.sgUf;
            }
            if (temp.enderecoFilial?.municipio?.uf) {
                temp.enderecoFilial.uf = temp.enderecoFilial.municipio.uf?.sgUf;
            }
            if (temp.enderecoPessoa && temp.enderecoPessoa.municipio) {
                temp.enderecoPessoa.uf = temp.enderecoPessoa.municipio.uf?.sgUf;
            }
            return temp;
        }

        if (service.cdServico && SERVICES_PROPERTIES[service.cdServico].formInitialValues) {
            let initValue = SERVICES_PROPERTIES[service.cdServico].formInitialValues;
            if (registrationType === 'person') {
                initValue = registrationPersonPayload;
                initValue.deCpf = registrationUserInfo.deCpfCnpj || '';
                initValue.nmCompleto = registrationUserInfo.nmUsuario || '';
            }

            const initialAddress = {};

            if (registrationUserInfo && Object.keys(registrationUserInfo).length > 0) {
                if (registrationUserInfo.municipio) {
                    initialAddress.municipio = registrationUserInfo.municipio;
                    initialAddress.uf = registrationUserInfo.municipio.uf?.sgUf;
                    initialAddress.cdMunicipio = registrationUserInfo.cdMunicipio
                        ? registrationUserInfo.cdMunicipio
                        : '';
                }
                if (registrationUserInfo.deBairro) initialAddress.deBairro = registrationUserInfo.deBairro;
                if (registrationUserInfo.deCep) initialAddress.deCep = registrationUserInfo.deCep;
                if (registrationUserInfo.deComplemento)
                    initialAddress.deComplemento = registrationUserInfo.deComplemento;
                if (registrationUserInfo.deCpfCnpj && registrationUserInfo.deCpfCnpj.length > 11)
                    initValue.deCnpjMatriz = registrationUserInfo.deCpfCnpj;
                if (registrationUserInfo.deEmail) initValue.deEmail = registrationUserInfo.deEmail;
                if (registrationUserInfo.deEndereco) initialAddress.deEndereco = registrationUserInfo.deEndereco;
                if (registrationUserInfo.deNumeroEndereco)
                    initialAddress.deNumeroEndereco = registrationUserInfo.deNumeroEndereco;
                if (registrationUserInfo.deTelefone) initValue.deTelefone = registrationUserInfo.deTelefone;
                if (registrationUserInfo.nmUsuario) initValue.deRazaoSocial = registrationUserInfo.nmUsuario;
            }

            if (registrationType === 'person') {
                initValue.enderecoPessoa = initialAddress;
            } else {
                initValue.enderecoMatriz = initialAddress;
            }

            return initValue;
        }
        return {};

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCompany, editing, request.jsonSolicitacao, service.cdServico, registrationType]);

    const onSave = useCallback(
        async (values, fromSubmit, isInternalForm) => {
            if (!canEdit) {
                if (service.cdServico !== SERVICES_CODES.INATIVACAO_CADASTRAL) {
                    return;
                } else {
                    if (request.situacao && !request.situacao?.boEdicaoPortalExterno) return;
                }
            }

            const valuesCopy = { ...values };

            if (service.cdServico !== SERVICES_CODES.INSCRICAO_CADASTRAL) {
                if (!valuesCopy.cdCertificado && activeCompany?.cdCertificado) {
                    valuesCopy.cdCertificado = activeCompany.cdCertificado;
                }
            }

            const documentsObject = {
                store: documentsStore,
                files: values.documentos,
            };

            let onlyContacts = valuesCopy.contatos.filter((contact) => {
                return (
                    (contact.peParticipacaoSocietaria === null || contact.peParticipacaoSocietaria === undefined) &&
                    !contact.boExcluido
                );
            });

            let contacts = [];

            let hasContactError = false;
            let hasPartnerError = false;

            if (!onlyContacts || onlyContacts.length === 0) hasContactError = true;
            else {
                onlyContacts.forEach((contact) => {
                    //CONTACT VALIDATION
                    const keys = Object.keys(contact);
                    if (
                        !keys.includes('nmPessoa') ||
                        contact.nmPessoa === '' ||
                        !keys.includes('deFuncao') ||
                        contact.deFuncao === '' ||
                        !keys.includes('deTelefoneMovel') ||
                        contact.deTelefoneMovel === '' ||
                        !keys.includes('deEmail') ||
                        contact.deEmail === ''
                    ) {
                        hasContactError = true;
                    }
                });
                contacts = onlyContacts;
            }

            if (!valuesCopy.cdTipoPessoaFisica) {
                if (!valuesCopy.socios || valuesCopy.socios.length === 0) {
                    if (valuesCopy.contatos) {
                        const partners = values.contatos.filter(
                            (contact) => !!contact.peParticipacaoSocietaria && !contact.boExcluido,
                        );
                        if (partners.length > 0) {
                            valuesCopy.socios = partners;
                        }
                    }
                }

                if (!valuesCopy.socios || valuesCopy.socios.length === 0) hasPartnerError = true;
                else {
                    valuesCopy.socios.forEach((contact) => {
                        //PARTNERS VALIDATION
                        const keys = Object.keys(contact);
                        if (
                            !keys.includes('nmPessoa') ||
                            contact.nmPessoa === '' ||
                            !keys.includes('deFuncao') ||
                            contact.deFuncao === '' ||
                            !keys.includes('peParticipacaoSocietaria') ||
                            contact.peParticipacaoSocietaria === ''
                        ) {
                            hasPartnerError = true;
                        }
                    });

                    contacts = [].concat(onlyContacts, valuesCopy.socios);

                    console.log({ contacts });

                    if (service.cdServico === SERVICES_CODES.INSCRICAO_CADASTRAL) {
                        contacts = contacts.map((c) => {
                            if (c.cdPessoaEmpresa) delete c.cdPessoaEmpresa;
                            return c;
                        });
                    }
                }

                if (valuesCopy.contatosExcluidos && valuesCopy.contatosExcluidos?.length > 0) {
                    contacts = [].concat(onlyContacts, valuesCopy.socios, valuesCopy.contatosExcluidos);
                }
            } else {
                if (valuesCopy.contatosExcluidos && valuesCopy.contatosExcluidos?.length > 0) {
                    contacts = [].concat(onlyContacts, valuesCopy.contatosExcluidos);
                }
            }

            valuesCopy.contatos = contacts;

            if (
                !validateCompanyRequest({
                    values: valuesCopy,
                    intl,
                    isInternalForm,
                    fromSubmit,
                    hasContactError,
                    hasPartnerError,
                })
            ) {
                return;
            }

            if (request?.situacao) valuesCopy.situacao = request.situacao;

            let dataToPayload = {
                cdServico: service.cdServico,
            };

            if (request && request.cdServico) {
                dataToPayload = { ...request };
            }

            const preparedPayload = preparePayload({
                values: valuesCopy,
                documents: documentsObject,
                params: dataToPayload,
                activeCompany,
                userAdmin,
                registrationUserInfo,
            });

            if (preparedPayload.errors && fromSubmit) {
                notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.MISSING_MANDATORY_INFOS' }), {
                    toastId: 'MISSING_MANDATORY_INFOS',
                    type: 'error',
                });
                return false;
            }

            dispatch(showLoading());

            let showMessage = false;
            if (!fromSubmit) showMessage = true;

            const response = await fetchSaveRequest({
                isFormData: true,
                preparedPayload,
                dispatch,
                intl,
                showMessage,
                cdService: service.cdServico,
            });
            if (response?.status === 200 && response?.data) {
                // I need to update the request to get the files ids
                if (response.data.jsonSolicitacao) {
                    const responseJson = JSON.parse(response.data.jsonSolicitacao);
                    await dispatch(extractFileFromJson(responseJson.documentos));
                }
                await dispatch(updateRequestDetails(response.data));
            }
            if (!fromSubmit) dispatch(hideLoading());
            return response;
        },
        [
            activeCompany,
            canEdit,
            dispatch,
            documentsStore,
            intl,
            registrationUserInfo,
            request,
            service.cdServico,
            userAdmin,
        ],
    );

    const onSubmit = useCallback(
        async (values) => {
            if (!canEdit) {
                if (service.cdServico !== SERVICES_CODES.INATIVACAO_CADASTRAL) {
                    return;
                } else {
                    if (request.situacao && !request.situacao?.boEdicaoPortalExterno) return;
                }
            }

            const currentStatusCode = request.cdSituacao;

            if (values.documentos?.length === 0) {
                notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.MISSING_MANDATORY_INFOS' }), {
                    toastId: 'MISSING_MANDATORY_INFOS',
                    type: 'warning',
                });
                return false;
            }

            //save before finish
            try {
                const responseSave = await onSave(values, true);
                if (responseSave && responseSave.status === 200) {
                    const response = await requestEndpoints.finishRequest(responseSave.data.cdSolicitacao);
                    if (response.status === 200) {
                        notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.SUCCESSFUL_FINISHED_REQUEST' }), {
                            toastId: 'SUCCESSFUL_FINISHED_REQUEST',
                            type: 'success',
                        });

                        if (currentStatusCode === STATUS_CODES.AGUARDANDO_RESOLUCAO_PENDENCIAS) {
                            await alertsEndpoint.deleteAlert({ cdSolicitacao: request.cdSolicitacao });
                        }
                    }
                    resetAndGo();
                }
            } catch (error) {
                errorHandler(error.response?.status, error.response?.data?.message);
            }
            dispatch(hideLoading());
        },
        [
            canEdit,
            dispatch,
            intl,
            onSave,
            request.cdSituacao,
            request.cdSolicitacao,
            request.situacao,
            resetAndGo,
            service.cdServico,
        ],
    );

    const contextProvider = useMemo(() => {
        return {
            loadingDocuments,
            setLoadingDocuments,
            validateForm,
            onSubmit,
            onSave,
            onCancel,
            initialValues,
            validationSchema,
            canEdit,
            registrationType,
            setRegistrationType,
        };
    }, [
        loadingDocuments,
        setLoadingDocuments,
        validateForm,
        onSubmit,
        onSave,
        onCancel,
        initialValues,
        validationSchema,
        canEdit,
        registrationType,
    ]);

    return (
        <Container>
            <BreadCrumb onBack={() => handleOnBack()} breadCrumb={breadCrumb} rightInfo={rightInfo} />
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Paper className={classes.container} elevation={3}>
                        <FormServiceContext.Provider value={contextProvider}>
                            {registrationType === 'company' && <Company {...props} />}
                            {registrationType === 'person' && <Person {...props} />}
                        </FormServiceContext.Provider>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
}

export default injectIntl(withRequestInitiation(Registration));
